import React, { useEffect, useMemo, useState } from 'react';
import css from './styles.module.scss';
import { MoneyListItemDto } from '~/models/contract.dto';
import { MoneyCardFinancialSummary } from '~/features/dashboard/components/money-grid/money-card';
import { Box, ButtonBase, Divider, Drawer, IconButton, TextField, Typography } from '@material-ui/core';
import { ChipsFilter } from '~/common/chips-filter';
import moment from 'moment';
import { DateRange, DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { Search } from '~/common/search';
import { searchMoneyFilter } from '../../store/selectors';
import { getSearchMoneyFilterActions } from '../../store/actions';
import { useSelector } from 'react-redux';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { PaginatedBlock } from '~/features/dashboard/components/paginated-block';
import { useDateFormat } from '~/common/date-format';
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { MoneyListChart } from '~/models/money/MoneyList';
import { ChartPie } from '~/common/chartPie';
interface MoneyFinancialSummary {
    contracts: MoneyListItemDto[];
}

const ITEMS_PER_PAGE = 6;

enum DRAWER_TYPES {
    FILTER = 'FILTER',
}

export const MoneyFinancialSummary: React.FC<MoneyFinancialSummary> = ({ contracts: items }) => {
    const { selectedDateFormat } = useDateFormat();
    const dispatch = useThunkDispatch();

    const [itemsStatusFilter, setItemsStatusFilter] = useState<string[]>([]);
    const [itemsTypesFilter, setItemsTypesFilter] = useState<string[]>([]);
    const [chartData, setChartData] = useState<MoneyListChart[]>([]);
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);
    const [searchValue, setSearchValue] = useState('');
    const getSearchDealValue = useSelector(searchMoneyFilter) || '';
    const [sumAmount, setSumAmount] = useState<number>(0);

    const types = useMemo(() => {
        const tmpTypes: string[] = [];
        for (const item of items) {
            if (item.sourceType.trim() && !tmpTypes.includes(item.sourceType)) {
                tmpTypes.push(item.sourceType);
            }
        }
        return tmpTypes;
    }, [items]);

    const statuses = useMemo(() => {
        const tmpStatuses: string[] = [];
        for (const item of items) {
            if (item.invStatus.trim() && !tmpStatuses.includes(item.invStatus)) {
                tmpStatuses.push(item.invStatus);
            }
        }
        return tmpStatuses;
    }, [items]);

    const onChangeSearchFilter = (value: any) => {
        dispatch(getSearchMoneyFilterActions.success({ searchMoneyFilter: value }));
    };

    useEffect(() => {
        if (items.length < ITEMS_PER_PAGE) {
            onChangeSearchFilter('');
            setItemsStatusFilter([]);
            setItemsTypesFilter([]);
            setDateFilter([null, null]);
        }
    }, [items]); // eslint-disable-line

    useEffect(() => {
        if (getSearchDealValue !== undefined) {
            setSearchValue(getSearchDealValue);
        }
    }, [getSearchDealValue]);

    const filteredItems: MoneyListItemDto[] = useMemo(() => {
        const [minDate, maxDate] = dateFilter;
        if (itemsTypesFilter.length || itemsStatusFilter.length || searchValue || (minDate && maxDate)) {
            return items.filter(item => {
                if (itemsTypesFilter.length && !itemsTypesFilter.includes(item.sourceType)) {
                    return false;
                }
                if (itemsStatusFilter.length && !itemsStatusFilter.includes(item.invStatus)) {
                    return false;
                }
                if (searchValue) {
                    const title = item.contractTitle.toLowerCase();
                    const search = searchValue.toLowerCase();
                    return title.includes(search);
                }
                if (minDate && maxDate && item.invDate) {
                    const compareDate = moment(item.invDate);
                    compareDate.isSameOrBefore(maxDate) && compareDate.isSameOrAfter(minDate);
                }
                return true;
            });
        }
        return items;
    }, [searchValue, items, itemsTypesFilter, dateFilter, itemsStatusFilter]);

    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };

    const openFilterDrawer = () => {
        openDrawer(DRAWER_TYPES.FILTER);
    };

    useEffect(() => {
        let sumInvAmount = 0;
        setChartData(
            types.map(item => {
                let sum = 0;
                filteredItems
                    .filter(({ sourceType }) => {
                        return sourceType === item;
                    })
                    .map(({ invAmount }) => {
                        return (sum += invAmount);
                    });
                sumInvAmount += sum;
                return {
                    id: item,
                    label: item,
                    value: sum,
                    color: 'hsl(230, 70%, 50%)',
                };
            })
        );
        setSumAmount(Number(sumInvAmount.toFixed(2)));
    }, [filteredItems, types]);

    return (
        <Box className={css.module}>
            <Box className={css.filtersContainer}>
                <ButtonBase>
                    <FilterListIcon className={css.filtersIcon} onClick={openFilterDrawer} />
                </ButtonBase>
                {items.length > ITEMS_PER_PAGE && (filteredItems.length > ITEMS_PER_PAGE || searchValue || dateFilter[0] || dateFilter[1]) && (
                    <Search search={getSearchDealValue} onSearchChange={onChangeSearchFilter} className={css.search} />
                )}
            </Box>
            <Box style={{ width: '40%', position: 'relative', height: '450px' }}>
                <Typography variant={'h3'} align={'center'}>
                    <b>2021</b> NET REVENUE
                </Typography>
                <Typography variant={'h4'} align={'center'} style={{ color: 'red', marginTop: '20px' }}>
                    ${sumAmount}
                </Typography>
                <ChartPie chartData={chartData} />
            </Box>
            <Box style={{ width: '60%' }}>
                <Typography variant={'h6'} style={{ color: 'grey' }}>
                    Last payments from deals:
                </Typography>
                <PaginatedBlock
                    data={filteredItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    keyName="assetCode"
                    renderItem={(item, key) => <MoneyCardFinancialSummary key={key} data={item} />}
                    centeredPagination
                />
            </Box>
            <Box className={css.DrawerContainer}>
                <Drawer variant="persistent" anchor="right" open={drawerData.isOpen} id="deal-filter-drawer" className={css.rightDrawer}>
                    <Box className={css.contentWrapper}>
                        <Box className={css.closeDrawer}>
                            <IconButton onClick={closeDrawer}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                        </Box>
                        <Divider />
                        {drawerData.type === DRAWER_TYPES.FILTER && (
                            <div className={css.chipFilterContainer}>
                                <LocalizationProvider dateAdapter={MomentUtils}>
                                    <DateRangePicker
                                        startText="Start Date"
                                        endText="End Date"
                                        value={dateFilter}
                                        inputFormat={selectedDateFormat}
                                        onChange={newValue => setDateFilter(newValue)}
                                        renderInput={(startProps, endProps) => (
                                            <React.Fragment>
                                                <TextField {...startProps} className={css.datePickerInput} variant="standard" />
                                                <DateRangeDelimiter> to </DateRangeDelimiter>
                                                <TextField {...endProps} className={css.datePickerInput} variant="standard" />
                                            </React.Fragment>
                                        )}
                                    />
                                </LocalizationProvider>
                                {types && (
                                    <Box className={css.chipFilterWrapper}>
                                        <Box pb={1}>
                                            <Typography variant="body2" className={css.bold}>
                                                My money type filters:
                                            </Typography>
                                        </Box>
                                        <ChipsFilter chips={types} filters={itemsTypesFilter} onFilterChange={setItemsTypesFilter} className={css.chipFilter} />
                                    </Box>
                                )}
                                {statuses && (
                                    <Box className={css.chipFilterWrapper}>
                                        <Box pb={1}>
                                            <Typography variant="body2" className={css.bold}>
                                                My money status filters:
                                            </Typography>
                                        </Box>
                                        <ChipsFilter
                                            chips={statuses}
                                            filters={itemsStatusFilter}
                                            onFilterChange={setItemsStatusFilter}
                                            className={css.chipFilter}
                                        />
                                    </Box>
                                )}
                            </div>
                        )}
                    </Box>
                </Drawer>
            </Box>
        </Box>
    );
};
